<script lang="ts">
// Packages
import { defineComponent, nextTick, type PropType } from 'vue';
import { liteClient } from 'algoliasearch/lite';
import { history as historyRouter } from 'instantsearch.js/es/lib/routers';

// Directives
import dropdown, { closeDropdown } from '@vue-directives/dropdown';

// Styles (for dev)
// import 'instantsearch.css/themes/reset.css';
// import 'instantsearch.css/themes/satellite.css';

// Components
import {
  AisClearRefinements,
  AisConfigure,
  AisCurrentRefinements,
  AisHits,
  AisInstantSearch,
  AisSearchBox,
  AisSortBy,
  AisStats,
  AisStateResults,
} from 'vue-instantsearch/vue3/es';
import RefinementPanel from '@vue-components/RefinementPanel.vue';
import SearchPagination from './SearchPagination.vue';

type RefinementList = { [key: string]: string[] };
type SortOption = {
  value: string;
  label: string;
};

type UiState = {
  [indexName: string]: {
    refinementList?: RefinementList;
    page?: number;
    query?: string;
    sortBy?: string;
  };
};
type UiRoute = Record<string, string /* | string[] | number */>;

const router = historyRouter<UiRoute>();

type Refinement = {
  label: string;
  attribute: string;
};

export default defineComponent({
  components: {
    AisClearRefinements,
    AisConfigure,
    AisCurrentRefinements,
    AisHits,
    AisInstantSearch,
    AisSearchBox,
    AisSortBy,
    AisStats,
    AisStateResults,
    RefinementPanel,
    SearchPagination,
  },
  directives: {
    dropdown,
  },
  props: {
    searchApiKey: {
      type: String,
      required: true,
    },
    appId: {
      type: String,
      required: true,
    },
    indexName: {
      type: String,
      required: true,
    },
    defaultIndexName: {
      type: String,
      required: false,
      default: null,
    },
    currentCategory: {
      type: String,
      required: true,
    },
    refinements: {
      type: Array as PropType<Refinement[]>,
      required: true,
    },
    sortOptions: {
      type: Array as PropType<SortOption[]>,
      required: false,
      default: () => [],
    },
    heading: {
      type: String,
      required: false,
      default: 'Search the hub',
    },
    headingElement: {
      type: String,
      required: false,
      default: 'h2',
    },
  },
  data() {
    return {
      searchClient: liteClient(this.appId, this.searchApiKey),
      userLat: null as number | null,
      userLng: null as number | null,
      locationQuery: null as string | null,
      useCurrentLocation: false,
      hasLocationError: false,
      sortIndex: this.sortOptions[0]?.value ?? this.indexName,
      lastTrackedQuery: '',
    };
  },
  computed: {
    defaultSortIndex() {
      return this.defaultIndexName ?? this.indexName;
    },
    routing() {
      const { indexName, currentCategory, defaultSortIndex, refinements, sortOptions } = this;
      return {
        router,
        stateMapping: {
          stateToRoute(uiState: UiState) {
            const indexUiState = uiState[indexName];
            const route: UiRoute = {};
            const { refinementList, page, query, sortBy } = indexUiState;
            if (refinementList) {
              Object.keys(refinementList).forEach((key) => {
                if (refinementList[key]) {
                  route[key] = (refinementList[key] as Array<string>).join('~');
                }
              });
            }
            if (query) {
              route.q = query;
            }
            if (page && page > 1) {
              route.p = page.toString();
            }
            if (sortBy) {
              route.s = sortBy.slice(indexName.length + 1);
            }
            return route;
          },
          routeToState(routeState: UiRoute): UiState {
            const prefixedSortBy = routeState.s ? `${indexName}_${routeState.s}` : null;
            const sortBy =
              prefixedSortBy &&
              sortOptions.find((sortOption) => sortOption.value === prefixedSortBy)
                ? prefixedSortBy
                : null;

            const refinementList = Object.fromEntries(
              refinements
                .map(({ attribute }) => [
                  attribute,
                  routeState[attribute] ? routeState[attribute].split('~') : undefined,
                ])
                .filter(([, val]) => val !== undefined),
            );

            // Force the insight category for the current entry to be pre-selected
            if (!refinementList.categories) {
              refinementList.categories = [currentCategory];
            } else if (!refinementList.categories.includes(currentCategory)) {
              refinementList.categories.push(currentCategory);
            }

            return {
              [indexName]: {
                query: routeState.q,
                refinementList,
                page: Math.max(Number(routeState.p) || 1, 1),
                sortBy: sortBy ?? defaultSortIndex,
              },
            };
          },
        },
      };
    },
  },
  methods: {
    closeDropdown,
    onPageChange() {
      nextTick(() => this.$el.scrollIntoView());
    },
    trackSearchState(query: string, nbHits: number): string {
      if (!query || query === this.lastTrackedQuery) return '';
      this.lastTrackedQuery = query;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'search_query', query, results_count: nbHits });
      return '';
    },
    trackResultClick(item: {
      title: string;
      url: string | null;
      externalUrl: string | null;
    }): void {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'search_result_click',
        result_title: item.title,
        result_url: item.url ?? item.externalUrl,
      });
    },
  },
});
</script>
<template>
  <ais-instant-search
    :search-client="searchClient"
    :routing="routing"
    :index-name="indexName"
    :class-names="{
      'ais-InstantSearch': 'text-ick-black-900',
    }"
  >
    <ais-configure :hits-per-page.camel="16" :index="sortIndex" />

    <!-- ====== START REFINEMENTS ====== -->
    <div class="-mt-6 rounded-b-3xl bg-ick-neutral-200 pt-18 pb-16">
      <div class="mx-auto max-w-304 px-5 md:px-9 xl:px-0">
        <component
          :is="headingElement"
          class="mb-18 font-barlow-condensed text-5.5xl/16 font-extrabold"
        >
          {{ heading }}
        </component>
        <div class="mb-8 flex flex-col items-center gap-10 md:flex-row md:items-end">
          <refinement-panel
            v-for="refinement in refinements"
            :key="refinement.attribute"
            class="md:basis-50"
            v-bind="refinement"
          />
          <!-- ====== START SEARCH BOX ====== -->
          <ais-search-box
            class=""
            :class-names="{
              'ais-SearchBox-form':
                'flex min-w-75 rounded-3xl border-2 border-ick-neutral-400 bg-white py-2.5 pr-4 pl-6 focus-within:[outline:auto] focus-within:outline-offset-4',
              'ais-SearchBox-input':
                'grow appearance-none text-sm font-medium outline-none focus:ring-0 focus:ring-offset-0',
              'ais-SearchBox-submit': 'ml-4',
              'ais-SearchBox-reset': 'hidden',
            }"
            placeholder="Search for your subject"
          >
            <template #submit-icon>
              <svg class="text-black" width="26" height="24" viewBox="0 0 26 24" fill="none">
                <g stroke="currentColor" stroke-width="2">
                  <circle
                    cx="11.2917"
                    cy="11.2917"
                    r="10.2867"
                    transform="rotate(-0.0250705 11.2917 11.2917)"
                  />
                  <line x1="18.9864" y1="17.7408" x2="24.361" y2="23.1154" />
                </g>
              </svg>
            </template>
          </ais-search-box>
          <!-- ====== END SEARCH BOX ====== -->
        </div>

        <div class="flex flex-wrap items-center gap-4 text-base/7.5 md:flex-nowrap md:gap-x-8">
          <!-- ====== START CURRENT REFINEMENTS ====== -->
          <ais-current-refinements>
            <template #default="{ items /* , createURL */ }">
              <ul class="flex flex-wrap justify-center gap-x-6 gap-y-2 md:gap-y-6">
                <template v-for="item in items" :key="item.attribute">
                  <li
                    v-for="refinement in item.refinements"
                    :key="
                      [
                        refinement.attribute,
                        refinement.type,
                        refinement.value,
                        refinement.operator,
                      ].join(':')
                    "
                    class="flex items-center rounded-full bg-ick-lime-500 px-4.5 py-1 text-base/7.5 text-ick-black"
                  >
                    {{ refinement.label }}
                    <button
                      class="ml-4 grid size-3.5 cursor-pointer place-items-center"
                      title="Remove this refinement"
                      type="button"
                      @click.prevent="item.refine(refinement)"
                    >
                      <svg height="14" width="14" viewBox="0 0 13 13" aria-hidden="true">
                        <line
                          x1="0.6"
                          y1="0.6"
                          x2="12.4"
                          y2="12.4"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                        />
                        <line
                          x1="0.6"
                          y1="12.4"
                          x2="12.4"
                          y2="0.6"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                        />
                      </svg>
                    </button>
                  </li>
                </template>
              </ul>
            </template>
          </ais-current-refinements>
          <!-- ====== END CURRENT REFINEMENTS ====== -->
          <!-- ====== START CLEAR REFINEMENTS ====== -->
          <ais-clear-refinements
            class="font-barlow-condensed text-lg font-extrabold text-ick-purple-400 transition-colors duration-300 hover:font-black hover:text-ick-purple-500"
            :class-names="{
              'ais-ClearRefinements-button': 'cursor-pointer',
              'ais-ClearRefinements-button--disabled': 'hidden',
            }"
          >
            <template #resetLabel>Clear filters</template>
          </ais-clear-refinements>
          <!-- ====== END CLEAR REFINEMENTS ====== -->
        </div>
      </div>
    </div>
    <!-- ====== END REFINEMENTS ====== -->

    <div class="mx-auto max-w-304 px-5 py-12 md:px-9 md:py-24 xl:px-0">
      <div class="mb-10 flex items-center justify-between gap-x-10">
        <!-- ====== START SORT BY ====== -->
        <ais-sort-by
          v-if="sortOptions.length"
          v-dropdown
          :items="sortOptions"
          class="dropdown group relative"
        >
          <template #default="{ items, currentRefinement, refine }">
            <button
              aria-expanded="false"
              class="dropdown-trigger btn flex items-center bg-ick-lime-500 py-1.5 pr-3 pl-4 text-sm/none font-bold whitespace-nowrap"
              type="button"
            >
              Sort by
              <svg class="ml-3 shrink-0" width="12" height="8" viewBox="0 0 12 8" fill="none">
                <path
                  d="M1 1L6 6L11 1"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </button>
            <ul
              class="absolute top-full left-0 z-1 mt-1.5 ml-4.5 hidden w-max max-w-176 min-w-60 rounded-xl bg-white py-2 shadow-md group-[.open]:block"
            >
              <li
                v-for="item in items"
                :key="item.value"
                :aria-current="currentRefinement === item.value"
              >
                <button
                  ref="sortButtons"
                  :data-sort="item.value"
                  class="block w-full cursor-pointer px-6 py-3 text-left font-barlow-condensed text-base/none font-extrabold transition-colors duration-300"
                  :class="[
                    currentRefinement === item.value
                      ? 'bg-ick-lime-500'
                      : 'hover:bg-ick-neutral-50',
                  ]"
                  type="button"
                  @click="
                    ((sortIndex = item.value),
                    refine(item.value),
                    closeDropdown(($event.target as HTMLElement).closest('.dropdown')!))
                  "
                >
                  {{ item.label }}
                </button>
              </li>
            </ul>
          </template>
        </ais-sort-by>
        <!-- ====== END SORT BY ====== -->
        <!-- ====== START STATS ====== -->
        <ais-stats class="text-sm/none font-bold">
          <template #default="{ nbHits }">
            <ais-state-results>
              <template #default="{ state, results: { hits } }">
                {{ trackSearchState(state.query, nbHits) }}
                Showing {{ hits.length }} of {{ nbHits }} results
              </template>
            </ais-state-results>
          </template>
        </ais-stats>
        <!-- ====== END STATS ====== -->
      </div>

      <!-- ====== START HITS ====== -->
      <ais-hits
        :class-names="{
          'ais-Hits': '',
          'ais-Hits-list':
            'relative grid grid-cols-1 justify-items-center gap-8 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
          'ais-Hits-item': 'max-w-full',
        }"
      >
        <template #item="props">
          <div class="contents" @click="trackResultClick(props.item)">
            <slot name="item" v-bind="{ ...props, currentCategory }" />
          </div>
        </template>
      </ais-hits>
      <!-- ====== END HITS ====== -->

      <!-- ====== START PAGINATION ====== -->
      <search-pagination @page-change="onPageChange"></search-pagination>
      <!-- ====== END PAGINATION ====== -->
    </div>
  </ais-instant-search>
</template>
